import type { SessionEvent } from '@/services/interfaces/SessionEvent';
import type { ConversationTurn } from '@/types/backend';
import { 
  Language, 
  detectLanguageFromText, 
  mapLanguageCodeToEnum 
} from '../services/interfaces/Translation';

// 🔑 核心接口定义
export interface ItemState {
  id: string;
  role: 'user' | 'assistant';
  status: 'in_progress' | 'completed' | 'incomplete' | 'error' | 'cancelled';
  transcript: string;
  deltaAccumulator: string; // 实时累积的 delta 文本
  finalTranscript?: string; // 最终确定的转录文本
  timestamp: string;
  conversationId?: string;
  // 关联关系
  previousItemId?: string;
  nextItemId?: string;
  // 预判的语言信息（可能会被最终的 AI 回复覆盖）
  predictedUserLanguage?: Language;
  predictedSpeaker?: 'userA' | 'userB';
  // 使用量信息
  usage?: any;
  // 合并相关字段
  isMerged?: boolean; // 标记此 item 是否已被合并到其他 item 中
  mergedFromItemIds?: string[]; // 如果此 item 是合并结果，记录被合并的 item IDs
  cancelReason?: string;
}

export interface TurnState {
  userItemId: string;
  assistantItemId?: string;
  userItem?: ItemState;
  assistantItem?: ItemState;
  isComplete: boolean;
  timestamp: string;
}

export interface ConversationState {
  lastProcessedIndex: number;
  currentLength: number;
  processedTurns: TurnState[];
  itemStates: Map<string, ItemState>;
  turnStates: Map<string, TurnState>; // key 是 userItemId
}

export interface LanguageConfig {
  sourceLanguage: Language; // 源语言（userA 说的语言）
  targetLanguage: Language; // 目标语言（userB 说的语言）
}

// 🔑 初始化函数
export function createInitialConversationState(): ConversationState {
  return {
    lastProcessedIndex: -1,
    currentLength: 0,
    processedTurns: [],
    itemStates: new Map(),
    turnStates: new Map()
  };
}

// 🔑 重置对话状态
export function resetConversationState(state: ConversationState): void {
  state.lastProcessedIndex = -1;
  state.currentLength = 0;
  state.processedTurns = [];
  state.itemStates.clear();
  state.turnStates.clear();
}

// 🔑 从用户转录中预判语言和说话者身份
function detectUserLanguageFromTranscript(transcript: string, languageConfig: LanguageConfig): {
  userLanguage: Language;
  speaker: 'userA' | 'userB';
} {
  const supportedLanguages = [languageConfig.sourceLanguage, languageConfig.targetLanguage];
  const detectedLanguage = detectLanguageFromText(transcript, supportedLanguages);
  
  console.log(`🎯 [detectUserLanguageFromTranscript] 预判结果:`, {
    transcript: transcript.substring(0, 100) + (transcript.length > 100 ? '...' : ''),
    detectedLanguage,
    sourceLanguage: languageConfig.sourceLanguage,
    targetLanguage: languageConfig.targetLanguage
  });
  
  if (detectedLanguage === languageConfig.sourceLanguage) {
    return {
      userLanguage: languageConfig.sourceLanguage,
      speaker: 'userA'
    };
  } else if (detectedLanguage === languageConfig.targetLanguage) {
    return {
      userLanguage: languageConfig.targetLanguage,
      speaker: 'userB'
    };
  } else {
    // 默认为源语言用户
    console.log(`⚠️ [detectUserLanguageFromTranscript] 检测失败，使用默认值 (源语言)`);
    return {
      userLanguage: languageConfig.sourceLanguage,
      speaker: 'userA'
    };
  }
}

// 🔑 根据AI回复确定用户语言和说话者身份 - 支持多语言和语言代码前缀
function determineUserFromAIResponse(assistantOutput: string, languageConfig: LanguageConfig): {
  userLanguage: Language;
  speaker: 'userA' | 'userB';
  cleanAssistantOutput: string;
} {
  if (!assistantOutput || assistantOutput.trim() === '') {
    // 默认值：如果没有AI回复，假设是源语言用户
    return {
      userLanguage: languageConfig.sourceLanguage,
      speaker: 'userA',
      cleanAssistantOutput: assistantOutput
    };
  }
  
  // 正则匹配：检查AI回复中的语言代码前缀（支持ISO 639-3三位字母格式）
  const langCodeMatch = assistantOutput.match(/^\s*\[([a-z]{3})\]\s*/i);
  
  if (langCodeMatch) {
    const aiResponseLangCode = langCodeMatch[1].toLowerCase();
    console.log(`🔍 [determineUserFromAIResponse] 语言代码前缀匹配:`, aiResponseLangCode);
    const cleanOutput = assistantOutput.replace(/^\s*\[([a-z]{3})\]\s*/i, '').trim();
    console.log(`🔍 [determineUserFromAIResponse] 清理后的输出:`, cleanOutput);
    
    // 将语言代码映射到 Language 枚举
    const aiResponseLanguage = mapLanguageCodeToEnum(aiResponseLangCode);
    
    if (aiResponseLanguage === languageConfig.sourceLanguage) {
      // AI 回复源语言，说明用户说的是目标语言
      return {
        userLanguage: languageConfig.targetLanguage,
        speaker: 'userB',
        cleanAssistantOutput: cleanOutput
      };
    } else if (aiResponseLanguage === languageConfig.targetLanguage) {
      // AI 回复目标语言，说明用户说的是源语言
      return {
        userLanguage: languageConfig.sourceLanguage,
        speaker: 'userA',
        cleanAssistantOutput: cleanOutput
      };
    }
  }
  
  // 如果没有找到明确的语言代码，使用 franc 进行内容检测
  const supportedLanguages = [languageConfig.sourceLanguage, languageConfig.targetLanguage];
  const detectedLanguage = detectLanguageFromText(assistantOutput, supportedLanguages);
  
  console.log(`🔍 [determineUserFromAIResponse] 语言检测回退 (使用 franc):`, {
    assistantOutput,
    detectedLanguage,
    inferred: detectedLanguage === languageConfig.sourceLanguage 
      ? 'AI说源语言，用户说目标语言' 
      : 'AI说目标语言，用户说源语言'
  });
  
  if (detectedLanguage === languageConfig.sourceLanguage) {
    // AI 回复源语言，用户说目标语言
    return {
      userLanguage: languageConfig.targetLanguage,
      speaker: 'userB',
      cleanAssistantOutput: assistantOutput
    };
  } else {
    // AI 回复目标语言，用户说源语言
    return {
      userLanguage: languageConfig.sourceLanguage,
      speaker: 'userA',
      cleanAssistantOutput: assistantOutput
    };
  }
}

/**
 * 智能合并用户 items - 处理被 VAD 误分割的连续语音
 * 
 * 合并条件：
 * 1. 当前 item 和前一个 item 都是 user 角色
 * 2. 前一个 item 没有对应的 assistant 回复
 * 3. 时间间隔在合理范围内（< 10秒）
 * 4. 语言和说话者身份一致
 * 5. 前一个 item 的转录内容不为空
 * 
 * 支持递归合并：如果前一个 item 本身也是合并结果，会继续向前查找
 */
function attemptUserItemMerging(
  currentItemId: string,
  itemStates: Map<string, ItemState>,
  turnStates: Map<string, TurnState>,
  languageConfig: LanguageConfig
): void {
  const currentItem = itemStates.get(currentItemId);
  if (!currentItem || currentItem.role !== 'user' || !currentItem.previousItemId) {
    return; // 不是用户 item 或没有前一个 item
  }

  const previousItem = itemStates.get(currentItem.previousItemId);
  if (!previousItem || previousItem.role !== 'user') {
    return; // 前一个不是用户 item
  }

  // 检查前一个 item 是否已经有 assistant 回复
  const previousTurn = turnStates.get(currentItem.previousItemId);
  if (!previousTurn || previousTurn.assistantItemId) {
    return; // 前一个 item 已经有 assistant 回复，不需要合并
  }

  // 检查时间间隔（防止合并相距太远的语音）
  const currentTime = new Date(currentItem.timestamp).getTime();
  const previousTime = new Date(previousItem.timestamp).getTime();
  const timeDiffSeconds = (currentTime - previousTime) / 1000;
  
  if (timeDiffSeconds > 10) {
    console.log(`⏰ [attemptUserItemMerging] 时间间隔过大 (${timeDiffSeconds.toFixed(1)}s)，跳过合并`);
    return; // 时间间隔超过 10 秒，可能不是连续语音
  }

  // 检查语言和说话者身份是否一致
  const currentSpeaker = currentItem.predictedSpeaker;
  const previousSpeaker = previousItem.predictedSpeaker;
  const currentLanguage = currentItem.predictedUserLanguage;
  const previousLanguage = previousItem.predictedUserLanguage;

  if (currentSpeaker !== previousSpeaker || currentLanguage !== previousLanguage) {
    console.log(`🗣️ [attemptUserItemMerging] 说话者或语言不一致，跳过合并:`, {
      current: { speaker: currentSpeaker, language: currentLanguage },
      previous: { speaker: previousSpeaker, language: previousLanguage }
    });
    return; // 说话者或语言不一致，不应该合并
  }

  // 检查前一个 item 的转录内容
  if (!previousItem.transcript || previousItem.transcript.trim() === '') {
    console.log(`📝 [attemptUserItemMerging] 前一个 item 转录为空，跳过合并`);
    return; // 前一个 item 没有有效内容
  }

  // 执行合并
  console.log(`🔗 [attemptUserItemMerging] 开始合并用户 items:`, {
    previousItemId: previousItem.id,
    currentItemId: currentItem.id,
    previousTranscript: previousItem.transcript,
    currentTranscript: currentItem.transcript,
    timeDiff: `${timeDiffSeconds.toFixed(1)}s`
  });

  // 合并转录内容（用空格连接，保持自然的语句结构）
  const mergedTranscript = `${previousItem.transcript.trim()} ${currentItem.transcript.trim()}`.trim();
  
  // 继承之前的合并历史（如果前一个 item 本身也是合并结果）
  const mergedFromItemIds = [
    ...(previousItem.mergedFromItemIds || []),
    previousItem.id
  ];
  
  // 更新当前 item 为合并后的内容
  currentItem.transcript = mergedTranscript;
  currentItem.mergedFromItemIds = mergedFromItemIds;
  
  // 标记前一个 item 为已合并
  previousItem.isMerged = true;
  
  // 删除前一个 item 的 turn（因为它已经被合并到当前 item 中）
  turnStates.delete(previousItem.id);
  
  // 更新当前 turn 的时间戳为最早的时间（保持时间顺序的正确性）
  const currentTurn = turnStates.get(currentItemId);
  if (currentTurn) {
    // 如果前一个 item 有合并历史，使用最早的时间戳
    const earliestTimestamp = mergedFromItemIds.reduce((earliest, itemId) => {
      const item = itemStates.get(itemId);
      if (!item) return earliest;
      const itemTime = new Date(item.timestamp).getTime();
      const earliestTime = new Date(earliest).getTime();
      return itemTime < earliestTime ? item.timestamp : earliest;
    }, previousItem.timestamp);
    
    currentTurn.timestamp = earliestTimestamp;
    currentTurn.userItem = currentItem; // 确保引用是最新的
  }

  console.log(`✅ [attemptUserItemMerging] 合并完成:`, {
    mergedItemId: currentItemId,
    mergedTranscript: mergedTranscript,
    removedTurnId: previousItem.id,
    totalMergedItems: mergedFromItemIds.length
  });

  // 递归检查：如果前一个 item 还有前一个 item，继续尝试合并
  // 这样可以处理连续多个被分割的语音片段
  if (previousItem.previousItemId && mergedFromItemIds.length < 5) {
    // 限制最大合并数量，防止无限递归
    // 更新当前 item 的 previousItemId 指向更早的 item
    currentItem.previousItemId = previousItem.previousItemId;
    
    // 递归尝试合并
    attemptUserItemMerging(currentItemId, itemStates, turnStates, languageConfig);
  } else if (mergedFromItemIds.length >= 5) {
    console.log(`⚠️ [attemptUserItemMerging] 达到最大合并数量限制 (${mergedFromItemIds.length})，停止递归合并`);
  }
}

/**
 * 检测被AI跳过的用户输入
 * 
 * 检测逻辑：
 * 1. 找到所有没有AI回复的用户输入
 * 2. 检查是否有后续的用户输入已经得到了AI回复
 * 3. 如果有，且时间间隔合理，则认为前面的输入被"跳过"了
 * 4. 将被跳过的输入标记为 incomplete 状态
 */
function detectSkippedUserInputs(
  turnStates: Map<string, TurnState>
): void {
  // 获取所有用户输入，按时间排序
  const userTurns = Array.from(turnStates.values())
    .filter(turn => turn.userItem && !turn.userItem.isMerged)
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  console.log(`🔍 [detectSkippedUserInputs] 检查 ${userTurns.length} 个用户输入`);

  for (let i = 0; i < userTurns.length - 1; i++) {
    const currentTurn = userTurns[i];
    const nextTurn = userTurns[i + 1];

    // 如果当前turn没有AI回复，但下一个turn有AI回复
    if (!currentTurn.assistantItemId && nextTurn.assistantItemId) {
      const currentTime = new Date(currentTurn.timestamp).getTime();
      const nextTime = new Date(nextTurn.timestamp).getTime();
      const timeDiffSeconds = (nextTime - currentTime) / 1000;

      // 如果时间间隔在合理范围内（30秒内），认为是被跳过的
      if (timeDiffSeconds <= 30) {
        console.log(`⚠️ [detectSkippedUserInputs] 检测到被跳过的用户输入，尝试宽松合并:`, {
          skippedItemId: currentTurn.userItemId,
          skippedTranscript: currentTurn.userItem?.transcript,
          nextItemId: nextTurn.userItemId,
          nextTranscript: nextTurn.userItem?.transcript,
          timeDiff: `${timeDiffSeconds.toFixed(1)}s`
        });

        // 执行宽松合并：将被跳过的输入合并到有AI回复的输入中
        const skippedItem = currentTurn.userItem;
        const nextItem = nextTurn.userItem;
        
        if (skippedItem && nextItem && skippedItem.transcript.trim() && nextItem.transcript.trim()) {
          console.log(`🔗 [detectSkippedUserInputs] 执行宽松合并:`, {
            skippedTranscript: skippedItem.transcript,
            nextTranscript: nextItem.transcript
          });
          
          // 合并转录内容
          const mergedTranscript = `${skippedItem.transcript.trim()} ${nextItem.transcript.trim()}`.trim();
          
          // 更新有AI回复的item
          nextItem.transcript = mergedTranscript;
          nextItem.mergedFromItemIds = [
            ...(nextItem.mergedFromItemIds || []),
            skippedItem.id
          ];
          
          // 标记被跳过的item为已合并
          skippedItem.isMerged = true;
          
          // 删除被跳过的turn
          turnStates.delete(currentTurn.userItemId);
          
          // 更新有AI回复的turn的时间戳为最早的时间
          const earlierTime = new Date(currentTurn.timestamp).getTime();
          const laterTime = new Date(nextTurn.timestamp).getTime();
          if (earlierTime < laterTime) {
            nextTurn.timestamp = currentTurn.timestamp;
          }
          
          console.log(`✅ [detectSkippedUserInputs] 宽松合并完成:`, {
            mergedTranscript,
            removedTurnId: currentTurn.userItemId,
            keptTurnId: nextTurn.userItemId
          });
        }
      }
    }
  }
}

// 🔑 更新助手项目状态和完成状态的通用方法
function updateAssistantItemAndTurn(
  itemId: string, 
  transcript: string, 
  status: string, 
  itemStates: Map<string, ItemState>,
  turnStates: Map<string, TurnState>,
  usage?: any,
  eventType?: string
) {
  console.log(`🤖 [updateAssistantItemAndTurn] ${eventType || '更新助手项目'}:`, {
    itemId,
    transcript: transcript.substring(0, 100) + (transcript.length > 100 ? '...' : ''),
    status,
    usage: usage ? '已保存' : '无'
  });
  
  // 更新 assistant item 的最终状态
  const itemState = itemStates.get(itemId);
  if (itemState) {
    itemState.transcript = transcript;
    
    // 映射状态：completed, incomplete, 其他都视为 in_progress
    if (status === 'completed') {
      itemState.status = 'completed';
    } else if (status === 'incomplete') {
      itemState.status = 'incomplete';
    } else {
      itemState.status = 'in_progress';
    }
    
    if (usage) {
      itemState.usage = usage; // 保存 usage 信息（如果有）
    }
    
    // 标记对应的 turn 为完成（只有 completed 状态才算真正完成）
    for (const turnState of turnStates.values()) {
      if (turnState.assistantItemId === itemId) {
        turnState.isComplete = status === 'completed';
        turnState.assistantItem = itemState;
        break;
      }
    }
  }
}

// 🔑 主要的对话处理函数 - 增量处理新事件
export function processIncrementalEvents(
  events: SessionEvent[], 
  config: LanguageConfig, 
  state: ConversationState
): ConversationTurn[] {
    // 计算需要处理的新事件
    // 注意：rtcEvents 是用 unshift 插入的，最新事件在前面
    // 需要 reverse() 让最老的事件在前面，这样 slice() 才能正确取到未处理的旧事件
    const newEvents = events.sort((a, b) => new Date(a.timestamp!).getTime() - new Date(b.timestamp!).getTime()).slice(state.lastProcessedIndex + 1);
  if (newEvents.length === 0) {
    console.log('无新事件，返回缓存结果');
    return state.processedTurns.map(turn => convertTurnStateToConversationTurn(turn, config));
  }
  
  console.log(`📊 处理 ${newEvents.length} 个增量事件: ${state.lastProcessedIndex + 1} -> ${events.length - 1} (${newEvents.length} 个新事件)`);

  // 获取当前状态的副本
  const itemStates = new Map(state.itemStates);
  const turnStates = new Map(state.turnStates);
  
  // 按时间戳排序事件，确保按正确顺序处理
  const sortedNewEvents = [...newEvents].sort((a, b) => {
    const timeA = new Date(a.timestamp || 0).getTime();
    const timeB = new Date(b.timestamp || 0).getTime();
    return timeA - timeB;
  });
  
  console.log(`🔄 [processConversationEvents] 增量处理 ${newEvents.length} 个新事件`, sortedNewEvents.map(event => event.type));
  
  // 处理新事件
  for (const event of sortedNewEvents) {
    switch (event.type) {
      case 'conversation.item.created': {
        const item = event.item;
        if (!item?.id) break;
        
        console.log(`📝 [processConversationEvents] 创建项目:`, {
          itemId: item.id,
          role: item.role,
          previousItemId: event.previous_item_id
        });
        
        // 创建或更新 item 状态
        const itemState: ItemState = {
          id: item.id,
          role: item.role as 'user' | 'assistant',
          status: item.status as 'in_progress' | 'completed',
          transcript: item.content?.[0]?.transcript || '',
          deltaAccumulator: '',
          timestamp: event.timestamp || new Date().toISOString(),
          conversationId: event.conversation_id,
          previousItemId: event.previous_item_id || undefined
        };
        
        itemStates.set(item.id, itemState);
        
        // 处理关联关系
        if (item.role === 'user') {
          // 用户消息：创建新的 turn
          const turnState: TurnState = {
            userItemId: item.id,
            userItem: itemState,
            isComplete: false,
            timestamp: itemState.timestamp
          };
          turnStates.set(item.id, turnState);
        } else if (item.role === 'assistant' && event.previous_item_id) {
          // 助手消息：关联到对应的用户消息
          const userTurn = turnStates.get(event.previous_item_id);
          if (userTurn) {
            userTurn.assistantItemId = item.id;
            userTurn.assistantItem = itemState;
            
            // 建立双向关联
            const userItem = itemStates.get(event.previous_item_id);
            if (userItem) {
              userItem.nextItemId = item.id;
            }
          }
        }
        break;
      }
      
      case 'conversation.item.input_audio_transcription.completed': {
        // 用户输入转录完成 - 提前预判语言和说话者
        const itemId = event.item_id;
        const transcript = (event.transcript || '').trim();
        
        console.log(`🗣️ [processConversationEvents] 用户转录完成:`, {
          itemId,
          transcript
        });
        
        // 预判用户语言和说话者身份
        const { userLanguage: predictedUserLanguage, speaker: predictedSpeaker } = 
          detectUserLanguageFromTranscript(transcript, config);
        
        console.log(`🔍 [processConversationEvents] 预判用户语言:`, {
          transcript,
          predictedUserLanguage,
          predictedSpeaker
        });
        
        // 更新对应的 item 状态
        const itemState = itemStates.get(itemId);
        if (itemState) {
          itemState.transcript = transcript;
          itemState.status = 'completed';
          itemState.predictedUserLanguage = predictedUserLanguage;
          itemState.predictedSpeaker = predictedSpeaker;
        } else {
          // 如果 item 还不存在，创建一个占位符
          const placeholderItem: ItemState = {
            id: itemId,
            role: 'user',
            status: 'completed',
            transcript: transcript,
            deltaAccumulator: '',
            timestamp: event.timestamp || new Date().toISOString(),
            conversationId: event.conversation_id,
            predictedUserLanguage: predictedUserLanguage,
            predictedSpeaker: predictedSpeaker
          };
          itemStates.set(itemId, placeholderItem);
          
          // 创建对应的 turn
          const turnState: TurnState = {
            userItemId: itemId,
            userItem: placeholderItem,
            isComplete: false,
            timestamp: placeholderItem.timestamp
          };
          turnStates.set(itemId, turnState);
        }
        
        // 🔄 智能合并检测：检查是否需要与前一个用户 item 合并
        attemptUserItemMerging(itemId, itemStates, turnStates, config);
        
        break;
      }
      
      case 'response.audio_transcript.delta': {
        // AI回复的增量文本
        const itemId = event.item_id;
        const delta = event.delta || '';
        
        // 更新对应的 assistant item
        const itemState = itemStates.get(itemId);
        if (itemState && itemState.role === 'assistant') {
          itemState.deltaAccumulator += delta;
          
          // 同时更新 turn 状态中的引用
          for (const turnState of turnStates.values()) {
            if (turnState.assistantItemId === itemId) {
              turnState.assistantItem = itemState;
              break;
            }
          }
        }
        break;
      }
      
      case 'response.content_part.done': {
        // AI内容部分完成 - 也可能包含完整的转录内容
        const itemId = event.item_id;
        const transcript = event.part?.transcript || event.content?.transcript || '';
        
        if (!itemId || !transcript) break;
        
        // 复用通用更新方法，使用智能更新（只有更长的内容才更新）
        const currentItemState = itemStates.get(itemId);
        const currentStatus = currentItemState?.status || 'in_progress';
        
        updateAssistantItemAndTurn(
          itemId,
          transcript,
          currentStatus, // 保持当前状态不变
          itemStates,
          turnStates,
          undefined, // 此事件不包含 usage 信息
          'AI内容部分完成'
        );
        break;
      }
      
      case 'response.output_item.done': {
        // AI单个输出项完成 - 提前处理完成状态
        const item = event.item;
        if (!item?.id) break;
        
        const assistantOutput = item.content?.[0]?.transcript || '';
        const status = item.status;
        
        updateAssistantItemAndTurn(
          item.id, 
          assistantOutput, 
          status, 
          itemStates,
          turnStates,
          undefined, // 此事件不包含 usage 信息
          'AI输出项完成'
        );
        break;
      }
      
      case 'response.done': {
        // AI回复完成 - 双重保险 + 保存 usage 信息
        const response = event.response;
        if (!response?.output?.[0]) break;
        
        const output = response.output[0];
        const assistantOutput = output.content?.[0]?.transcript || '';
        const status = response.status;
        const usage = response.usage; // 保存 usage 信息
        
        updateAssistantItemAndTurn(
          output.id, 
          assistantOutput, 
          status, 
          itemStates,
          turnStates,
          usage, // 传递 usage 信息
          'AI回复完成'
        );
        break;
      }
    }
  }

    
  // 构建最终的 ConversationTurn 数组
  const conversationTurns: ConversationTurn[] = [];
  
  // 检测被AI跳过的用户输入
  detectSkippedUserInputs(turnStates);
  
  for (const turnState of turnStates.values()) {
    try {
      conversationTurns.push(convertTurnStateToConversationTurn(turnState, config));
    } catch (error) {
      continue;
    }
  }
  
  // 按时间排序，最新的在前面
  const sortedTurns = conversationTurns.sort((a, b) => 
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
  
  // 更新状态
  state.itemStates = itemStates;
  state.turnStates = turnStates;
  state.lastProcessedIndex = events.length - 1;
  state.processedTurns = Array.from(turnStates.values());
  
  return sortedTurns;
}

// 转换 TurnState 到 ConversationTurn
function convertTurnStateToConversationTurn(turn: TurnState, config: LanguageConfig): ConversationTurn {
  const userItem = turn.userItem;
  const assistantItem = turn.assistantItem;
  
  if (!userItem) {
    throw new Error('TurnState must have userItem');
  }
  
  // 确定用户语言和说话者身份
  // 优先使用 AI 回复确定的语言，其次使用预判的语言
  const assistantOutput = assistantItem?.transcript || assistantItem?.deltaAccumulator || '';
  let finalUserLanguage: Language;
  let finalSpeaker: 'userA' | 'userB';
  let cleanAssistantOutput: string;
  
  if (assistantOutput && turn.isComplete) {
    // AI 回复完成，使用最终确定的语言
    const result = determineUserFromAIResponse(assistantOutput, config);
    finalUserLanguage = result.userLanguage;
    finalSpeaker = result.speaker;
    cleanAssistantOutput = result.cleanAssistantOutput;
     
      console.log(`✅ [processConversationEvents] 最终确定语言:`, {
        userItemId: userItem.id,
        finalUserLanguage,
        finalSpeaker,
        source: 'AI回复'
      });
  } else {
    // 使用预判的语言（用于快速显示 UI）
    finalUserLanguage = userItem.predictedUserLanguage || config.sourceLanguage;
    finalSpeaker = userItem.predictedSpeaker || 'userA';
    cleanAssistantOutput = assistantOutput;

      console.log(`⚡ [processConversationEvents] 使用预判语言:`, {
        userItemId: userItem.id,
        finalUserLanguage,
        finalSpeaker,
        source: '预判'
      });
  }
  
  const assistantLanguage = finalUserLanguage === config.sourceLanguage 
    ? config.targetLanguage 
    : config.sourceLanguage;
  
  // 确定状态
  let status: 'in_progress' | 'completed' | 'incomplete' | 'cancelled' | 'error' = 'in_progress';
  if (turn.isComplete) {
    status = 'completed';
  } else if (assistantItem) {
    // 根据 assistant item 的状态来确定 turn 状态
    if (assistantItem.status === 'incomplete') {
      status = 'incomplete';
    } else if (assistantItem.deltaAccumulator) {
      status = 'in_progress';
    }
  } else if (userItem.status === 'incomplete') {
    // 用户输入被标记为 incomplete（被AI跳过）
    status = 'incomplete';
  }
  
  return {
    id: userItem.id,
    session_id: userItem.conversationId || '', // 将在上层设置
    item_id: userItem.id,
    speaker: finalSpeaker,
    user_input: userItem.transcript,
    user_language: finalUserLanguage,
    assistant_output: status === 'completed' ? cleanAssistantOutput : (status === 'incomplete' ? cleanAssistantOutput : ''),
    assistant_language: assistantLanguage,
    status: status,
    timestamp: userItem.timestamp,
    usage_data: assistantItem?.usage ? JSON.stringify(assistantItem.usage) : null,
    merged_from_item_ids: userItem.mergedFromItemIds ? JSON.stringify(userItem.mergedFromItemIds) : null,
    // 前端特有的解析后字段
    deltaText: status === 'in_progress' ? assistantItem?.deltaAccumulator : undefined,
    cancelReason: userItem.cancelReason,
    usage: assistantItem?.usage,
    mergedFromItemIds: userItem.mergedFromItemIds
  };
} 